import { useSearchParams } from "react-router";
import { CalendarClock, ClipboardList, FileCode2, FileText, MessageSquareText, Receipt } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CadastrosOmieTab } from "@/components/finance/CadastrosOmieTab";
import { CompetenciasTab } from "@/components/finance/CompetenciasTab";
import { InviteTextsTab } from "@/components/finance/InviteTextsTab";
import { OrdensServicoTab } from "@/components/finance/OrdensServicoTab";
import { PropostasTab } from "@/components/finance/PropostasTab";
import { TemplatesTab } from "@/components/finance/TemplatesTab";
import { useUserPermissions } from "@/hooks/use-user-permissions";

const ABAS = ["propostas", "os", "cadastros-omie", "competencias", "textos-invite", "modelo"] as const;
type Aba = (typeof ABAS)[number];

function abaValida(valor: string | null, podeVerOs: boolean): Aba {
  if (valor === "os" && !podeVerOs) return "propostas";
  return ABAS.includes(valor as Aba) ? (valor as Aba) : "propostas";
}

export default function Financeiro() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { screens } = useUserPermissions();
  const podeVerOs = screens.includes("financeiro_os");
  const aba = abaValida(searchParams.get("aba"), podeVerOs);

  const trocarAba = (valor: string) => {
    setSearchParams(
      (atual) => {
        const proximo = new URLSearchParams(atual);
        proximo.set("aba", valor);
        return proximo;
      },
      { replace: true },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Financeiro"
        subtitle="Propostas, cadastros e ordens de serviço ligados ao Omie."
      />

      <Tabs value={aba} onValueChange={trocarAba} className="w-full">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="propostas" className="gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            Propostas
          </TabsTrigger>
          {podeVerOs && (
            <TabsTrigger value="os" className="gap-1.5">
              <Receipt className="h-3.5 w-3.5" />
              OS
            </TabsTrigger>
          )}
          <TabsTrigger value="cadastros-omie" className="gap-1.5">
            <ClipboardList className="h-3.5 w-3.5" />
            Cadastros Omie
          </TabsTrigger>
          <TabsTrigger value="competencias" className="gap-1.5">
            <CalendarClock className="h-3.5 w-3.5" />
            Competências
          </TabsTrigger>
          <TabsTrigger value="textos-invite" className="gap-1.5">
            <MessageSquareText className="h-3.5 w-3.5" />
            Textos de invite
          </TabsTrigger>
          <TabsTrigger value="modelo" className="gap-1.5">
            <FileCode2 className="h-3.5 w-3.5" />
            Modelo
          </TabsTrigger>
        </TabsList>

        <TabsContent value="propostas" className="mt-6">
          <PropostasTab />
        </TabsContent>

        {podeVerOs && (
          <TabsContent value="os" className="mt-6">
            <OrdensServicoTab />
          </TabsContent>
        )}

        <TabsContent value="cadastros-omie" className="mt-6">
          <CadastrosOmieTab />
        </TabsContent>

        <TabsContent value="competencias" className="mt-6">
          <CompetenciasTab />
        </TabsContent>

        <TabsContent value="textos-invite" className="mt-6">
          <InviteTextsTab />
        </TabsContent>

        <TabsContent value="modelo" className="mt-6">
          <TemplatesTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
